import React, { useCallback, useEffect, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { DCard } from './MarketplaceAtoms.js';
import { AD_SLOT_OPTIONS, buildAdSlotPromptSnippet, getAdSlotSizeHint } from '../../lib/adSlotsConfig.js';
import { fetchActiveAdCountsBySlot } from '../../lib/adminAdsService.js';

/**
 * Resumen por pantalla/slot: cuántos slides activos hay y acceso rápido para añadir.
 */
export default function AdminAdSlotsOverview({ slotFilter, onSlotFilter, onAddInSlot, onPromptCopied }) {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedSlot, setCopiedSlot] = useState(null);

  const copyPromptForSlot = async slotValue => {
    const text = buildAdSlotPromptSnippet(slotValue);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSlot(slotValue);
      onPromptCopied?.('Datos para prompt copiados al portapapeles');
      window.setTimeout(() => setCopiedSlot(s => (s === slotValue ? null : s)), 2500);
    } catch {
      onPromptCopied?.('No se pudo copiar; permite acceso al portapapeles o copia el texto manualmente.');
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setCounts(await fetchActiveAdCountsBySlot());
    } catch {
      setCounts({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <DCard style={{ padding: 14, marginBottom: 14, background: 'rgba(200,168,75,0.04)', borderColor: 'rgba(200,168,75,0.2)' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.gold, marginBottom: 6 }}>Pantallas y slots (carrusel)</div>
      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: '0 0 12px', lineHeight: 1.5 }}>
        Cada ubicación puede tener varios slides activos a la vez (orden con «Orden» en la tabla). En el hero del Mercado, las campañas
        visibles van <em>antes</em> del vídeo y banners por defecto. El contador «activos» solo incluye campañas con fechas vigentes hoy.
      </p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => onSlotFilter('')}
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            border: `0.5px solid ${!slotFilter ? C.gold : 'rgba(255,255,255,0.12)'}`,
            background: !slotFilter ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
            color: !slotFilter ? C.gold : 'rgba(255,255,255,0.65)',
            fontSize: 10,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Todos los slots
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
        {AD_SLOT_OPTIONS.map(slot => {
          const n = counts[slot.value] ?? 0;
          const filtered = slotFilter === slot.value;
          const hint = getAdSlotSizeHint(slot.value);
          return (
            <div
              key={slot.value}
              style={{
                padding: 12,
                borderRadius: 10,
                border: `0.5px solid ${filtered ? C.gold : 'rgba(255,255,255,0.1)'}`,
                background: filtered ? 'rgba(200,168,75,0.08)' : 'rgba(0,0,0,0.2)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{slot.label}</div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: n ? C.green : 'rgba(255,255,255,0.35)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {loading ? '…' : `${n} activo${n === 1 ? '' : 's'}`}
                </span>
              </div>
              <p style={{ margin: '8px 0 10px', fontSize: 9, color: 'rgba(200,168,75,0.75)', lineHeight: 1.4 }}>{hint}</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => onSlotFilter(filtered ? '' : slot.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 8,
                    border: '0.5px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  {filtered ? 'Ver todas' : 'Filtrar lista'}
                </button>
                <button
                  type="button"
                  onClick={() => onAddInSlot(slot.value)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 8,
                    border: '0.5px solid rgba(200,168,75,0.35)',
                    background: 'rgba(200,168,75,0.1)',
                    color: C.gold,
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  + Añadir slide
                </button>
                <button
                  type="button"
                  onClick={() => copyPromptForSlot(slot.value)}
                  title="Copia tamaños y contexto DreamVinyl para pegar en ChatGPT, Midjourney, etc."
                  style={{
                    padding: '5px 10px',
                    borderRadius: 8,
                    border: '0.5px solid rgba(120,160,255,0.4)',
                    background: copiedSlot === slot.value ? 'rgba(29,158,117,0.15)' : 'rgba(120,160,255,0.08)',
                    color: copiedSlot === slot.value ? C.green : 'rgba(180,200,255,0.95)',
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {copiedSlot === slot.value ? '✓ Copiado' : 'Prompt tamaños'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </DCard>
  );
}
